import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  CheckIcon, 
  XMarkIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  FunnelIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ClipboardDocumentIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';

const Orders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [shopOwners, setShopOwners] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  const [formData, setFormData] = useState({
    shop_owner_id: '',
    items: [],
    payment_type: 'credit',
    amount_paid: 0,
    due_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    delivery_date: '',
    delivery_instructions: '',
    discount: 0,
    notes: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    from_date: '',
    to_date: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  // Check if user can create consignments (only owners and managers)
  const canCreateConsignment = () => {
    return user?.user_type === 'owner' || user?.user_type === 'manager';
  };

  useEffect(() => {
    fetchOrders();
    fetchShopOwners();
    fetchProducts();
  }, [pagination.page, pagination.per_page, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        status: filters.status || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined
      };

      const response = await api.get('/orders/', { params });
      setOrders(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Failed to fetch orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShopOwners = async () => {
    try {
      const response = await api.get('/users/shop-owners/', {
        params: { per_page: 1000 }
      });
      setShopOwners(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch shop owners:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/', { params: { per_page: 1000 } });
      setProducts(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleCreateOrder = async () => {
    try {
      const submitData = {
        ...formData,
        amount_paid: formData.payment_type === 'cash' ? formData.amount_paid : 0
      };
      await api.post('/orders/', submitData);
      toast.success('Order created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create order');
    }
  };

  const handleCancelOrder = async () => {
    try {
      await api.post(`/orders/${selectedOrder.id}/cancel`);
      toast.success('Order cancelled');
      setShowCancelDialog(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel order');
    }
  };

  const handleAddPayment = async () => {
    try {
      await api.post(`/orders/${selectedOrder.id}/add-payment`, paymentData);
      toast.success('Payment added successfully');
      setShowPaymentModal(false);
      setPaymentData({
        amount: '',
        payment_method: 'cash',
        reference_number: '',
        notes: ''
      });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add payment');
    }
  };

  const handleCompleteDelivery = async () => {
    try {
      await api.post(`/orders/${selectedOrder.id}/complete-delivery`);
      toast.success('Delivery completed and sales recorded');
      setShowDetailsModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to complete delivery');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: '',
        quantity_cases: 1,
        unit_price: 0
      }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'unit_price' || field === 'quantity_cases') {
      newItems[index].total = (newItems[index].quantity_cases || 0) * (newItems[index].unit_price || 0);
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const resetForm = () => {
    setFormData({
      shop_owner_id: '',
      items: [],
      payment_type: 'credit',
      amount_paid: 0,
      due_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      delivery_date: '',
      delivery_instructions: '',
      discount: 0,
      notes: ''
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity_cases * item.unit_price), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal - (formData.discount || 0);
  };

  const columns = [
    {
      header: 'Order #',
      accessorKey: 'order_number',
      cell: ({ getValue }) => (
        <span className="font-medium text-primary-600">{getValue()}</span>
      )
    },
    {
      header: 'Date',
      accessorKey: 'order_date',
      cell: ({ getValue }) => format(new Date(getValue()), 'dd MMM yyyy')
    },
    {
      header: 'Customer',
      accessorKey: 'shop_owner_name',
      cell: ({ getValue }) => (
        <div>
          <div className="font-medium">{getValue()}</div>
          <div className="text-xs text-gray-500">Created by: {selectedOrder?.creator_name}</div>
        </div>
      )
    },
    {
      header: 'Items',
      accessorKey: 'items_count',
      cell: ({ getValue }) => `${getValue()} items`
    },
    {
      header: 'Total',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => `ZMW ${getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Paid',
      accessorKey: 'amount_paid',
      cell: ({ getValue }) => `ZMW ${getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Balance',
      accessorKey: 'balance',
      cell: ({ getValue }) => (
        <span className={getValue() > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
          ZMW {getValue()?.toLocaleString() || 0}
        </span>
      )
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />
    },
    {
      header: 'Delivery',
      accessorKey: 'delivery_date',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : 'Not set'
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedOrder(row.original);
              setShowDetailsModal(true);
            }}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {/* Only show Create Consignment button for managers and owners */}
          {row.original.status === 'pending' && canCreateConsignment() && (
            <button
              onClick={() => {
                setSelectedOrder(row.original);
                setShowDetailsModal(true);
              }}
              className="text-purple-600 hover:text-purple-900"
              title="Create Consignment"
            >
              <TruckIcon className="h-5 w-5" />
            </button>
          )}
          {row.original.status !== 'completed' && row.original.balance > 0 && (
            <button
              onClick={() => {
                setSelectedOrder(row.original);
                setShowPaymentModal(true);
              }}
              className="text-green-600 hover:text-green-900"
              title="Add Payment"
            >
              <CreditCardIcon className="h-5 w-5" />
            </button>
          )}
          {row.original.status === 'pending' && canCreateConsignment() && (
            <button
              onClick={() => {
                setSelectedOrder(row.original);
                setShowCancelDialog(true);
              }}
              className="text-red-600 hover:text-red-900"
              title="Cancel Order"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Orders</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage customer orders, track fulfillment, and process payments.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Order
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">From Date</label>
              <input
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.from_date}
                onChange={(e) => setFilters({...filters, from_date: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Date</label>
              <input
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.to_date}
                onChange={(e) => setFilters({...filters, to_date: e.target.value})}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={fetchOrders}
              className="btn-primary"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="mt-8">
        <DataTable
          data={orders}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Create Order Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New Order"
        size="lg"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleCreateOrder();
        }}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Shop Owner *</label>
              <select
                required
                className="form-input"
                value={formData.shop_owner_id}
                onChange={(e) => setFormData({...formData, shop_owner_id: e.target.value})}
              >
                <option value="">Select Shop Owner</option>
                {shopOwners.map(owner => (
                  <option key={owner.shop_owner_id} value={owner.shop_owner_id}>
                    {owner.business_name} - {owner.town}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label mb-2">Items *</label>
              {formData.items.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <select
                    required
                    className="flex-1 form-input"
                    value={item.product_id}
                    onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                  >
                    <option value="">Select Product</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} ({product.bottles_per_case} bottles/case)
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    required
                    className="w-24 form-input"
                    placeholder="Cases"
                    value={item.quantity_cases}
                    onChange={(e) => updateItem(index, 'quantity_cases', parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className="w-32 form-input"
                    placeholder="Unit Price"
                    value={item.unit_price}
                    onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value))}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-800 px-2"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addItem}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                + Add Item
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Payment Type *</label>
                <select
                  required
                  className="form-input"
                  value={formData.payment_type}
                  onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                >
                  <option value="cash">Cash</option>
                  <option value="credit">Credit</option>
                </select>
              </div>

              {formData.payment_type === 'cash' && (
                <div>
                  <label className="form-label">Amount Paid</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={calculateTotal()}
                    className="form-input"
                    value={formData.amount_paid}
                    onChange={(e) => setFormData({...formData, amount_paid: parseFloat(e.target.value)})}
                  />
                </div>
              )}

              {formData.payment_type === 'credit' && (
                <div>
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    min={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Delivery Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div>
                <label className="form-label">Discount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={calculateSubtotal()}
                  className="form-input"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Delivery Instructions</label>
              <textarea
                className="form-input"
                rows="2"
                value={formData.delivery_instructions}
                onChange={(e) => setFormData({...formData, delivery_instructions: e.target.value})}
                placeholder="Special instructions for delivery..."
              />
            </div>

            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>ZMW {calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Discount:</span>
                <span>-ZMW {formData.discount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold pt-2 border-t">
                <span>Total:</span>
                <span className="text-primary-600">ZMW {calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={formData.items.length === 0}
              >
                Create Order
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Order Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`Order Details - ${selectedOrder?.order_number}`}
        size="lg"
      >
        {selectedOrder && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Order Date</p>
                <p className="font-medium">{format(new Date(selectedOrder.order_date), 'dd MMM yyyy')}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Status</p>
                <StatusBadge status={selectedOrder.status} />
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Delivery Date</p>
                <p className="font-medium">{selectedOrder.delivery_date ? format(new Date(selectedOrder.delivery_date), 'dd MMM yyyy') : 'Not set'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Created By</p>
                <p className="font-medium">{selectedOrder.creator_name || 'System'}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Product</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Cases</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Unit Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{item.product_name}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.quantity_cases}</td>
                      <td className="px-4 py-3 text-sm text-right">ZMW {item.unit_price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right">ZMW {item.total_price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        {item.delivered_cases} / {item.quantity_cases}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span>ZMW {selectedOrder.subtotal?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Discount:</span>
                <span>-ZMW {selectedOrder.discount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between font-bold pt-2 border-t">
                <span>Total:</span>
                <span className="text-primary-600">ZMW {selectedOrder.total_amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="text-green-600">ZMW {selectedOrder.amount_paid?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Balance:</span>
                <span className={selectedOrder.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                  ZMW {selectedOrder.balance?.toLocaleString()}
                </span>
              </div>
            </div>

            {selectedOrder.delivery_instructions && (
              <div className="bg-yellow-50 p-3 rounded">
                <p className="text-sm font-medium text-yellow-800">Delivery Instructions</p>
                <p className="text-sm text-yellow-700">{selectedOrder.delivery_instructions}</p>
              </div>
            )}

            {/* Only show Create Consignment button for managers and owners */}
            {selectedOrder.status === 'pending' && canCreateConsignment() && (
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    navigate(`/consignments?order_id=${selectedOrder.id}`);
                  }}
                  className="btn-primary"
                >
                  <TruckIcon className="h-4 w-4 mr-2" />
                  Create Consignment
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setPaymentData({
            amount: '',
            payment_method: 'cash',
            reference_number: '',
            notes: ''
          });
        }}
        title={`Add Payment - ${selectedOrder?.order_number}`}
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleAddPayment();
        }}>
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm text-gray-600">Order Total: ZMW {selectedOrder?.total_amount?.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Amount Paid: ZMW {selectedOrder?.amount_paid?.toLocaleString()}</p>
              <p className="text-lg font-bold text-red-600">Balance: ZMW {selectedOrder?.balance?.toLocaleString()}</p>
            </div>

            <div>
              <label className="form-label">Payment Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={selectedOrder?.balance}
                required
                className="form-input"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value)})}
              />
            </div>

            <div>
              <label className="form-label">Payment Method *</label>
              <select
                required
                className="form-input"
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
              >
                <option value="cash">Cash</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="check">Check</option>
              </select>
            </div>

            <div>
              <label className="form-label">Reference Number</label>
              <input
                type="text"
                className="form-input"
                value={paymentData.reference_number}
                onChange={(e) => setPaymentData({...paymentData, reference_number: e.target.value})}
              />
            </div>

            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="2"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Add Payment
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelOrder}
        title="Cancel Order"
        message={`Are you sure you want to cancel order ${selectedOrder?.order_number}?`}
        confirmText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Orders;